import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plane, Users, DollarSign, FileText, TrendingUp, AlertCircle, Building2 } from "lucide-react";
import { formatNumber, formatCurrency } from "@/utils/formatters";
import PopupDisplay from "@/components/PopupDisplay";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to format dates consistently
const formatFlightDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  
  // If already in DD/MM/YYYY format, return as-is
  if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    return dateStr;
  }
  
  // Handle YYYY-MM-DD format
  if (dateStr.includes('-')) {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }
  
  return dateStr;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_flights: 0,
    total_passengers: 0,
    total_revenue: 0,
    unpaid_invoices: 0,
    today_flights: 0,
    tomorrow_flights: 0
  });
  const [pendingRegistrations, setPendingRegistrations] = useState(0);
  const [pendingAgencies, setPendingAgencies] = useState(0);
  const [pendingFlights, setPendingFlights] = useState(0);
  const [pendingUsers, setPendingUsers] = useState(0);
  const [myPendingFlights, setMyPendingFlights] = useState([]);
  const [myRejectedFlights, setMyRejectedFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchStats();
    if (currentUser.role === 'admin') {
      fetchPendingCount();
      fetchPendingFlightsCount();
      fetchPendingUsersCount();
      
      // Auto-refresh pending counts every 30 seconds for admin
      const interval = setInterval(() => {
        fetchPendingFlightsCount();
        fetchPendingUsersCount();
        fetchPendingCount();
      }, 30000); // 30 seconds
      
      return () => clearInterval(interval);
    } else if (currentUser.role === 'agency' || currentUser.role === 'staff') {
      fetchMyFlightStatus();
      
      // Auto-refresh for agencies/staff every 30 seconds
      const interval = setInterval(() => {
        fetchMyFlightStatus();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const [statsRes, flightsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/flights`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      let flights = flightsRes.data;
      
      // Backend already filters flights for staff users - no need for additional filtering here
      
      // Helper function to format date as DD/MM/YYYY
      const formatDateDDMMYYYY = (date) => {
        return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
      };
      
      // Calculate tomorrow's flights (using DD/MM/YYYY format)
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = formatDateDDMMYYYY(tomorrow);
      const tomorrowFlights = flights.filter(f => f.flight_date === tomorrowStr).length;
      
      // Calculate today's flights (using DD/MM/YYYY format)
      const todayStr = formatDateDDMMYYYY(new Date());
      const todayFlights = flights.filter(f => f.flight_date === todayStr).length;
      
      // For staff, show only their flight counts (backend already filtered)
      if (currentUser.role === 'staff') {
        setStats({
          total_flights: flights.length,
          total_passengers: flights.reduce((sum, f) => sum + (f.passenger_count || 0), 0),
          today_flights: todayFlights,
          tomorrow_flights: tomorrowFlights,
          // Hide revenue for staff
          total_revenue: 0,
          unpaid_invoices: 0
        });
      } else {
        setStats({
          ...statsRes.data,
          tomorrow_flights: tomorrowFlights
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
    setLoading(false);
  };

  const fetchPendingCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const [agenciesRes, usersRes] = await Promise.all([
        axios.get(`${API}/agencies`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users/pending`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const pendingAgenciesCount = agenciesRes.data.filter(a => a.status === 'pending_approval').length;
      const pendingUsersCount = usersRes.data.length;
      setPendingAgencies(pendingAgenciesCount);
      setPendingRegistrations(pendingAgenciesCount + pendingUsersCount);
    } catch (error) {
      console.error("Failed to fetch pending count");
    }
  };

  const fetchPendingFlightsCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/flights/pending`, { headers: { Authorization: `Bearer ${token}` } });
      setPendingFlights(response.data.length);
    } catch (error) {
      console.error("Failed to fetch pending flights count");
    }
  };

  const fetchPendingUsersCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/pending`, { headers: { Authorization: `Bearer ${token}` } });
      // Filter only agency users
      const agencyPendingUsers = response.data.filter(u => u.role === 'agency');
      setPendingUsers(agencyPendingUsers.length);
    } catch (error) {
      console.error("Failed to fetch pending users count");
    }
  };


  const fetchMyFlightStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/flights/my-status`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setMyPendingFlights(response.data.pending || []);
      setMyRejectedFlights(response.data.rejected || []);
    } catch (error) {
      console.error("Failed to fetch flight status");
    }
  };




  const handleCardClick = (cardTitle) => {
    switch (cardTitle) {
      case "Total Flights":
      case "My Assigned Flights":
        navigate('/planning');
        break;
      case "Total Passengers":
      case "Total Passengers I'll Handle":
        navigate('/flights');
        break;
      case "Unpaid Invoices":
        // Agency users go to Pay Invoice page, others go to agency statements
        if (currentUser.role === 'agency') {
          navigate('/pay-invoice');
        } else {
          navigate('/agency-statements?filter=unpaid');
        }
        break;
      case "Today's Flights":
      case "My Flights Today":
        navigate('/planning?filter=today');
        break;
      case "Tomorrow's Flights":
      case "My Flights Tomorrow":
        navigate('/planning?filter=tomorrow');
        break;
      case "Pending Flights":
        navigate('/pending-tasks');
        break;
      case "Pending Agency Requests":
        navigate('/agencies');
        break;
      case "Pending User Requests":
        navigate('/agencies');
        break;
      default:
        break;
    }
  };

  const statCards = [
    {
      title: currentUser.role === 'staff' ? "My Assigned Flights" : "Total Flights",
      value: formatNumber(stats.total_flights),
      icon: Plane,
      color: "#FFD700",
      bgGradient: "linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(255, 215, 0, 0.1) 100%)",
      clickable: true,
      roles: ["admin", "operations", "accounting", "agency", "staff"]
    },
    {
      title: currentUser.role === 'staff' ? "Total Passengers I'll Handle" : "Total Passengers",
      value: formatNumber(stats.total_passengers),
      icon: Users,
      color: "#C0C0C0",
      bgGradient: "linear-gradient(135deg, rgba(192, 192, 192, 0.2) 0%, rgba(192, 192, 192, 0.1) 100%)",
      clickable: true,
      roles: ["admin", "operations", "accounting", "agency", "staff"]
    },
    {
      title: "Total Revenue",
      value: formatCurrency(stats.total_revenue),
      icon: DollarSign,
      color: "#FFD700",
      bgGradient: "linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(255, 215, 0, 0.1) 100%)",
      clickable: false,
      roles: ["admin", "operations", "accounting"] // Hide from agency and staff users
    },
    {
      title: "Unpaid Invoices",
      value: formatNumber(stats.unpaid_invoices),
      icon: FileText,
      color: "#FF6B6B",
      bgGradient: "linear-gradient(135deg, rgba(255, 107, 107, 0.15) 0%, rgba(255, 107, 107, 0.05) 100%)",
      clickable: true,
      roles: ["admin", "accounting", "agency"] // Now visible to agency users too
    },
    {
      title: currentUser.role === 'staff' ? "My Flights Today" : "Today's Flights",
      value: formatNumber(stats.today_flights),
      icon: TrendingUp,
      color: "#C0C0C0",
      bgGradient: "linear-gradient(135deg, rgba(192, 192, 192, 0.2) 0%, rgba(192, 192, 192, 0.1) 100%)",
      clickable: true,
      roles: ["admin", "operations", "accounting", "agency", "staff"]
    },
    {
      title: currentUser.role === 'staff' ? "My Flights Tomorrow" : "Tomorrow's Flights",
      value: formatNumber(stats.tomorrow_flights),
      icon: TrendingUp,
      color: "#60A5FA",
      bgGradient: "linear-gradient(135deg, rgba(96, 165, 250, 0.2) 0%, rgba(96, 165, 250, 0.1) 100%)",
      clickable: true,
      roles: ["admin", "operations", "accounting", "agency", "staff"]
    },
    {
      title: "Pending Flights",
      value: formatNumber(pendingFlights),
      icon: AlertCircle,
      color: "#F59E0B",
      bgGradient: "linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(245, 158, 11, 0.1) 100%)",
      clickable: true,
      roles: ["admin"] // Admin only
    },
    {
      title: "Pending Agency Requests",
      value: formatNumber(pendingAgencies),
      icon: Building2,
      color: "#8B5CF6",
      bgGradient: "linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.1) 100%)",
      clickable: true,
      roles: ["admin"] // Admin only
    },
    {
      title: "Pending User Requests",
      value: formatNumber(pendingUsers),
      icon: Users,
      color: "#EC4899",
      bgGradient: "linear-gradient(135deg, rgba(236, 72, 153, 0.2) 0%, rgba(236, 72, 153, 0.1) 100%)",
      clickable: true,
      roles: ["admin"] // Admin only
    },
    {
      title: "My Pending Flights",
      value: formatNumber(myPendingFlights.length),
      icon: AlertCircle,
      color: "#F59E0B",
      bgGradient: "linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(245, 158, 11, 0.1) 100%)",
      clickable: false,
      roles: ["agency"] // Agency only
    }
    // Removed "Rejected Flights" card - agencies no longer see rejected flights
  ].filter(card => card.roles.includes(currentUser.role));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p style={{ color: '#C0C0C0' }}>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      {/* Popup Display */}
      <PopupDisplay location="dashboard" />
      
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#FFD700' }}>
            {currentUser.role === 'staff' ? 'My Dashboard' : 'Dashboard'}
          </h1>
          <p style={{ color: '#C0C0C0' }}>
            {currentUser.role === 'staff' 
              ? `Welcome back, ${currentUser.full_name || 'Staff Member'}`
              : 'Welcome to Air Transport Trade VIP Service Management'
            }
          </p>
        </div>
        {currentUser.role === 'admin' && pendingRegistrations > 0 && (
          <div 
            className="flex items-center gap-2 px-4 py-2 rounded cursor-pointer hover:opacity-80 transition-all"
            style={{ background: 'rgba(255, 215, 0, 0.2)', border: '2px solid #FFD700' }}
            onClick={() => navigate('/agencies')}
          >
            <div className="w-3 h-3 rounded-full animate-pulse" style={{ background: '#FFD700' }}></div>
            <span style={{ color: '#FFD700', fontWeight: 'bold' }}>
              {pendingRegistrations} Pending Registration{pendingRegistrations !== 1 ? 's' : ''}
            </span>
          </div>
        )}
        {currentUser.role === 'agency' && stats.unpaid_invoices > 0 && (
          <div 
            className="flex items-center gap-2 px-4 py-2 rounded cursor-pointer hover:opacity-80 transition-all"
            style={{ background: 'rgba(255, 107, 107, 0.2)', border: '2px solid #FF6B6B' }}
            onClick={() => navigate('/pay-invoice')}
          >
            <div className="w-3 h-3 rounded-full animate-pulse" style={{ background: '#FF6B6B' }}></div>
            <span style={{ color: '#FF6B6B', fontWeight: 'bold' }}>
              {stats.unpaid_invoices} Unpaid Invoice{stats.unpaid_invoices !== 1 ? 's' : ''} - Click to Pay
            </span>
          </div>
        )}
      </div>
      
      {/* Unpaid Invoices Alert Banner for Agency Users */}
      {currentUser.role === 'agency' && stats.unpaid_invoices > 0 && (
        <div 
          className="mb-6 p-4 rounded-lg cursor-pointer hover:opacity-90 transition-all"
          style={{ background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.15) 0%, rgba(255, 107, 107, 0.05) 100%)', border: '2px solid rgba(255, 107, 107, 0.3)' }}
          onClick={() => navigate('/pay-invoice')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(255, 107, 107, 0.2)' }}>
                <FileText className="w-6 h-6" style={{ color: '#FF6B6B' }} />
              </div>
              <div>
                <h3 className="font-bold text-lg" style={{ color: '#FF6B6B' }}>
                  You have {stats.unpaid_invoices} unpaid invoice{stats.unpaid_invoices !== 1 ? 's' : ''}
                </h3>
                <p className="text-sm" style={{ color: '#C0C0C0' }}>
                  Click here to view and pay your outstanding invoices
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded" style={{ background: '#FF6B6B' }}>
              <span style={{ color: '#FFFFFF', fontWeight: 'bold' }}>Pay Now →</span>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <Card
              key={index}
              className={`card-hover fade-in-up ${card.clickable ? 'cursor-pointer transform transition-all hover:scale-105' : ''}`}
              data-testid={`stat-card-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => card.clickable && handleCardClick(card.title)}
              style={{
                background: card.bgGradient,
                border: `1px solid ${card.color}33`,
                backdropFilter: 'blur(12px)',
                animationDelay: `${index * 0.1}s`
              }}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium" style={{ color: '#E8E8E8' }}>
                  {card.title}
                  {card.clickable && <span className="ml-2 text-xs" style={{ color: card.color }}>→</span>}
                </CardTitle>
                <Icon className="h-5 w-5" style={{ color: card.color }} />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold" style={{ color: card.color }}>
                  {card.value}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Access - Hidden for staff users */}
      {currentUser.role !== 'staff' && (
        <div className="mt-8 p-6 rounded-lg" style={{ background: 'rgba(30, 42, 74, 0.6)', border: '1px solid rgba(212, 175, 55, 0.2)', backdropFilter: 'blur(12px)' }}>
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#FFD700' }}>Quick Access</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a href="/flights" className="p-4 rounded-lg hover:scale-105" style={{ background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.3)', transition: 'transform 0.2s' }} data-testid="quick-link-flights">
              <Plane className="h-6 w-6 mb-2" style={{ color: '#FFD700' }} />
              <h3 className="font-semibold" style={{ color: '#E8E8E8' }}>Manage Flights</h3>
              <p className="text-sm" style={{ color: '#C0C0C0' }}>Add and manage flight information</p>
            </a>
            <a href="/reports" className="p-4 rounded-lg hover:scale-105" style={{ background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.3)', transition: 'transform 0.2s' }} data-testid="quick-link-reports">
              <FileText className="h-6 w-6 mb-2" style={{ color: '#FFD700' }} />
              <h3 className="font-semibold" style={{ color: '#E8E8E8' }}>Operational Reports</h3>
              <p className="text-sm" style={{ color: '#C0C0C0' }}>View and create reports</p>
            </a>
            <a href="/invoices" className="p-4 rounded-lg hover:scale-105" style={{ background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.3)', transition: 'transform 0.2s' }} data-testid="quick-link-invoices">
              <DollarSign className="h-6 w-6 mb-2" style={{ color: '#FFD700' }} />
              <h3 className="font-semibold" style={{ color: '#E8E8E8' }}>Invoicing</h3>
              <p className="text-sm" style={{ color: '#C0C0C0' }}>Generate and manage invoices</p>
            </a>
          </div>
        </div>
      )}

      {currentUser.role === 'admin' && (
        <div className="mt-8 p-6 rounded-lg" style={{ background: 'rgba(30, 42, 74, 0.6)', border: '1px solid rgba(255, 215, 0, 0.3)', backdropFilter: 'blur(12px)' }}>
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#FFD700' }}>Admin Management</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a href="/admin-users" className="p-4 rounded-lg hover:scale-105" style={{ background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.3)', transition: 'transform 0.2s' }} data-testid="quick-link-admin-users">
              <Users className="h-6 w-6 mb-2" style={{ color: '#FFD700' }} />
              <h3 className="font-semibold" style={{ color: '#E8E8E8' }}>Manage Users & Roles</h3>
              <p className="text-sm" style={{ color: '#C0C0C0' }}>Create users, edit roles, change passwords</p>
            </a>
          </div>
        </div>
      )}


      {/* Agency & Staff Flight Status Section */}
      {(currentUser.role === 'agency' || currentUser.role === 'staff') && (
        <div className="mt-8 space-y-6">
          {/* Pending Flights */}
          <div className="p-6 rounded-lg" style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#F59E0B' }}>
              <AlertCircle className="h-6 w-6" />
              {currentUser.role === 'staff' ? 'My Pending Flights' : 'Pending Flights Awaiting Approval'} ({myPendingFlights.length})
            </h2>
            <p className="text-sm mb-4" style={{ color: '#B0B0B0' }}>
              {currentUser.role === 'staff' 
                ? 'These are flights assigned to you that are pending admin approval.'
                : 'These flights are waiting for admin approval and will appear in your planning once approved.'
              }
            </p>
            {myPendingFlights.length > 0 ? (
              <div className="space-y-3">
                {myPendingFlights.map(flight => (
                  <div key={flight.id} className="p-4 rounded" style={{ background: 'rgba(10, 17, 40, 0.5)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-bold text-lg" style={{ color: '#E8E8E8' }}>{flight.flight_number}</div>
                        <div className="text-sm" style={{ color: '#B0B0B0' }}>
                          {formatFlightDate(flight.flight_date)} • {flight.departure_airport} → {flight.arrival_airport} • {flight.passenger_count} PAX
                          {flight.agency_name && ` • ${flight.agency_name}`}
                        </div>
                      </div>
                      <div className="px-3 py-1 rounded text-sm" style={{ background: 'rgba(245, 158, 11, 0.2)', color: '#F59E0B' }}>
                        Pending
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center p-8" style={{ color: '#B0B0B0' }}>
                <p>No pending flights at the moment</p>
                <p className="text-sm mt-2">
                  {currentUser.role === 'staff' 
                    ? 'All flights assigned to you have been approved!'
                    : 'All your flights have been approved!'
                  }
                </p>
              </div>
            )}
          </div>

          {/* Rejected Flights Section for Agencies */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold" style={{ color: '#FFFFFF' }}>
                My Rejected Flights
              </h2>
              <span style={{ 
                color: '#FFD700', 
                fontSize: '12px',
                background: 'rgba(255, 215, 0, 0.1)',
                padding: '4px 12px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 215, 0, 0.3)'
              }}>
                📅 Last 15 Days History
              </span>
            </div>
            {myRejectedFlights.length > 0 ? (
              <div className="space-y-2">
                {myRejectedFlights.map((flight) => (
                  <Card 
                    key={flight.id} 
                    style={{ 
                      background: 'rgba(139, 0, 0, 0.1)', 
                      borderColor: '#8B0000',
                      cursor: 'pointer'
                    }}
                    onClick={() => navigate('/flights')}
                    className="hover:bg-opacity-20 transition-all"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded" style={{ background: '#8B0000' }}>
                            <AlertCircle className="h-5 w-5" style={{ color: '#FFFFFF' }} />
                          </div>
                          <div>
                            <p style={{ color: '#FFFFFF', fontWeight: 600 }}>
                              {flight.flight_number || 'N/A'}
                            </p>
                            <p style={{ color: '#B0B0B0', fontSize: '14px' }}>
                              {formatFlightDate(flight.flight_date)} at {flight.flight_time || 'N/A'}
                              {flight.departure_airport && flight.arrival_airport && 
                                ` • ${flight.departure_airport} → ${flight.arrival_airport}`
                              }
                            </p>
                            {flight.rejection_reason && (
                              <p style={{ color: '#FF6B6B', fontSize: '12px', marginTop: '4px' }}>
                                ❌ Reason: {flight.rejection_reason}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <span style={{ 
                            background: '#8B0000', 
                            color: '#FFFFFF', 
                            padding: '4px 12px', 
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            REJECTED
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div style={{ 
                background: 'rgba(74, 90, 138, 0.2)', 
                padding: '20px', 
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <p style={{ color: '#B0B0B0' }}>No rejected flights</p>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default Dashboard;
